import React, { useState, useRef, useEffect } from 'react';
import { colors } from './theme';

function ExplodingWord({ word }) {
  const [exploding, setExploding] = useState(false);
  const wordRef = useRef(null);

  const handleMouseEnter = () => {
    setExploding(true);
  };

  const handleAnimationComplete = () => {
    setExploding(false);
  };

  return (
    <span 
      ref={wordRef}
      onMouseEnter={handleMouseEnter} 
      style={{ 
        position: 'relative',
        display: 'inline-block',
        color: exploding ? 'transparent' : colors.subtitleColor,
        fontFamily: 'maitree, sans-serif',
        whiteSpace: 'nowrap',
      }}
    >
      {word}
      {exploding && wordRef.current && (
        <ParticleCanvas 
          text={word} 
          onComplete={handleAnimationComplete}
          element={wordRef.current}
        />
      )}
    </span>
  );
}

function ParticleCanvas({ text, onComplete, element }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!element) return;
    
    requestAnimationFrame(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      const rect = element.getBoundingClientRect();
      
      if (rect.width === 0 || rect.height === 0) return;

      const computedStyle = window.getComputedStyle(element);
      const fontSize = parseInt(computedStyle.fontSize);
      const fontFamily = computedStyle.fontFamily;

      canvas.width = Math.ceil(rect.width);
      canvas.height = Math.ceil(rect.height);

      canvas.style.position = 'absolute';
      canvas.style.left = '0';
      canvas.style.top = '0';
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      canvas.style.pointerEvents = 'none';

      ctx.textBaseline = 'middle';
      ctx.textAlign = 'center';
      ctx.fillStyle = '#777';
      ctx.font = `${fontSize}px ${fontFamily}`;
      ctx.fillText(text, canvas.width / 2, canvas.height / 2);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const particles = [];
      const gap = 2;

      for (let y = 0; y < canvas.height; y += gap) {
        for (let x = 0; x < canvas.width; x += gap) {
          const index = (y * canvas.width + x) * 4;
          const alpha = imageData.data[index + 3];
          if (alpha > 128) {
            particles.push({
              x,
              y,
              originX: x,
              originY: y,
              offsetX: (Math.random() - 0.5) * 15,
              offsetY: (Math.random() - 0.5) * 15,
            });
          }
        }
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const explosionDuration = 300;
      const holdDuration = 300;
      const reassembleDuration = 300;
      const totalDuration = explosionDuration + holdDuration + reassembleDuration;
      const startTime = performance.now();

      function easeOutQuad(t) {
        return t * (2 - t);
      }

      function easeInQuad(t) {
        return t * t;
      }

      let animationFrameId;

      function animate() {
        const currentTime = performance.now();
        const elapsed = currentTime - startTime;

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        particles.forEach(p => {
          let progress, currentX, currentY;
          if (elapsed < explosionDuration) {
            progress = easeOutQuad(elapsed / explosionDuration);
            currentX = p.originX + p.offsetX * progress;
            currentY = p.originY + p.offsetY * progress;
          } else if (elapsed < explosionDuration + holdDuration) {
            currentX = p.originX + p.offsetX;
            currentY = p.originY + p.offsetY;
          } else if (elapsed < totalDuration) {
            progress = easeInQuad((elapsed - explosionDuration - holdDuration) / reassembleDuration);
            currentX = (p.originX + p.offsetX) * (1 - progress) + p.originX * progress;
            currentY = (p.originY + p.offsetY) * (1 - progress) + p.originY * progress;
          } else {
            onComplete();
            return;
          }

          ctx.fillStyle = '#777';
          ctx.beginPath();
          ctx.arc(currentX, currentY, 1, 0, 2 * Math.PI);
          ctx.fill();
        });

        if (elapsed < totalDuration) {
          animationFrameId = requestAnimationFrame(animate);
        }
      }

      if (particles.length > 0) {
        animationFrameId = requestAnimationFrame(animate);
      } else {
        onComplete();
      }

      return () => {
        cancelAnimationFrame(animationFrameId);
      };
    });
  }, [text, onComplete, element]);

  return (
    <canvas
      ref={canvasRef}
      style={{ 
        position: 'absolute',
        left: '0',
        top: '0',
        pointerEvents: 'none',
      }}
    />
  );
}

function ExplodingText({ text }) {
  const words = text.split(/\s+/);
  
  return (
    <p style={{
      color: '#777',
      fontFamily: 'maitree, sans-serif',
      position: 'relative',
      lineHeight: '1.6',
      letterSpacing: '0.02em',
      textAlign: 'justify',
      margin: 0,
      padding: 0,
      overflow: 'hidden',
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.25em',
    }}>
      {words.map((word, index) => (
        <ExplodingWord key={index} word={word} />
      ))}
    </p>
  );
}

export default ExplodingText;
